import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { NotFoundError } from 'src/shared/error';

import { isEligibleForTrafficPercentage } from 'src/app/shared/utils/featureRollout';
import { PETITION_ACTIVITY_COMMENT_ROLES } from 'src/app/shared/utils/petitionActivity';

import type { ActivityNewCountsSuccess, RootPageDataParams, RootPageDataReturn, RootPagePetitionBase } from '../types';

import { getPetitionMetadata } from './utilities/getPetitionMetadata';
import { getPetitionPromotionStats } from './utilities/getPetitionPromotionStats';
import { getPetitionReadyToSharePosts } from './utilities/getPetitionReadyToSharePosts';
import { getPetitionRecentSigners } from './utilities/getPetitionRecentSigners';
import { getPetitionTotalViews } from './utilities/getPetitionTotalViews';
import { getStarterContactDmConfig } from './utilities/getStarterContactDmConfig';
import { getStarterRootPageData } from './utilities/getStarterRootPageData';
import { shouldLoadShareRecommendations } from './utilities/shouldLoadShareRecommendations';

// If the query fails, we still return default values to avoid breaking the page.
function getDefaultActivityNewCounts(
	activityNewCounts: RootPagePetitionBase['activityNewCounts'],
): ActivityNewCountsSuccess {
	if (activityNewCounts.__typename === 'PetitionActivityNewCountsSuccess') {
		return activityNewCounts;
	}

	return {
		__typename: 'PetitionActivityNewCountsSuccess',
		totalNewCount: 0,
		totalNewCountDisplay: '0',
		comments: {
			newCount: 0,
		},
		signatures: {
			newCount: 0,
		},
	};
}

// eslint-disable-next-line max-lines-per-function
export async function getRootPageData(
	utilityContext: UtilityContext,
	{ slug, countryCode, locale }: RootPageDataParams,
): Promise<RootPageDataReturn> {
	const {
		gql: { fetch },
		i18n,
	} = utilityContext;

	// Get a timestamp that's 24 hours before current time to send to GET petition tasks query
	const completedSince = new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString();

	const [{ emailEnabled, emailSubjectLine, emailBody }, includeShareRecommendations, recentSignersConnection] =
		await Promise.all([
			getStarterContactDmConfig(utilityContext),
			shouldLoadShareRecommendations(utilityContext),
			getPetitionRecentSigners(utilityContext, { slug }),
		]);
	const { data } = await getStarterRootPageData(fetch, {
		slug,
		completedSince,
		locale,
		countryCode,
		emailEnabled,
		commentsRoles: PETITION_ACTIVITY_COMMENT_ROLES,
	});

	if (!data.petition) {
		throw new NotFoundError();
	}

	const { totalViews, totalViewsLocalized } = getPetitionTotalViews(data.petition, i18n);
	const petition = {
		...data.petition,
		...getPetitionMetadata(data.petition),
		decisionMakersConnection: {
			totalCount: data.petition.decisionMakersConnection.totalCount,
			nodes: data.petition.decisionMakersConnection.edges.map((edge) => ({
				...edge.node,
				targetPetitionAssociationData: edge.associationData,
			})),
		},
		totalViews,
		totalViewsLocalized,
		promotionStats: getPetitionPromotionStats(data.petition, data.viewer, i18n),
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		tagsConnection: data.petition.tagsConnection || { nodes: [] },
		emailDm: { emailSubjectLine, emailBody },
		recentSignersConnection,
		activityNewCounts: getDefaultActivityNewCounts(data.petition.activityNewCounts),
	};

	const readyToSharePosts = await getPetitionReadyToSharePosts({ fetch, slugOrId: slug });

	const starterUserId = data.petition.user.id;

	return {
		petition,
		viewer: data.viewer,
		shareRecommendations: {
			shareRecommendationsEnabled: includeShareRecommendations,
		},
		iOSBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppIosMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		androidBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppAndroidMwebDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		desktopBannerV2Enabled: isEligibleForTrafficPercentage(
			data.featureConfigs.mobileAppCombinedDesktopDownloadBannerV2?.traffic_percentage,
			starterUserId,
		),
		readyToSharePosts,
		featureConfigs: data.featureConfigs,
	};
}
